/*
 * Pure logic behind the activity feed: normalize legacy rows onto the current
 * event shape, classify events for filtering, describe them in prose with
 * Clarify-style change lines, resolve where each one deep-links, collapse
 * bursts into groups and format times.
 */
import dayjs from "dayjs";
import {
  AlertTriangle,
  Bot,
  Briefcase,
  CalendarClock,
  ClipboardList,
  FileSpreadsheet,
  FileSignature,
  FileText,
  FolderTree,
  Globe,
  Home,
  ListChecks,
  Mail,
  MessageSquare,
  Package,
  PenLine,
  Plane,
  Search,
  Settings2,
  User,
  Users,
  type LucideIcon
} from "lucide-react";

import {
  DRAFT_TYPE_LABELS,
  type ActivityActionKind,
  type ActivityActorKind,
  type ActivityEntityKind,
  type ActivityEntityRef,
  type DraftType,
  type ForeignNationalActivityLogEvent,
  type ListForeignNationalActivityLogParams
} from "../../../../api/types";
import { ASSISTANT_PERSONA, caseyName } from "../../../agents/types";
import { caseWorkspacePath, type CaseRef } from "../../../caseIde/routes";
import type { ClientTab } from "../../urlState";

export type UserProfileGetter = (userId: string) =>
  | {
      first_name?: string | null;
      last_name?: string | null;
      email?: string | null;
      avatar_data_url?: string | null;
    }
  | undefined;

type Change = { field: string; old?: unknown | null; new?: unknown | null };

const str = (value: unknown): string =>
  value == null ? "" : String(value).trim();

export const safeValueLabel = (value: unknown): string => {
  if (value == null) return "—";
  if (typeof value === "string") return value.trim() || "—";
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  try {
    const raw = JSON.stringify(value);
    if (!raw) return "—";
    return raw.length > 180 ? `${raw.slice(0, 177)}...` : raw;
  } catch {
    return "—";
  }
};

// Server kinds ------------------------------------------------------------------
//
// Mirrors `caseblink.activity.actions.entity_kind_for` / `action_kind_for` so
// rows written before the server denormalized these still classify the same.

const ENTITY_PREFIXES: Array<[string, ActivityEntityKind]> = [
  ["foreign_national.reminder.", "reminder"],
  ["foreign_national.dependent.", "dependent"],
  ["foreign_national.address_history.", "address_history"],
  ["foreign_national.employment_history.", "employment_history"],
  ["foreign_national.immigration_status_history.", "immigration_history"],
  ["foreign_national.immigration_entry_exit_history.", "immigration_history"],
  ["foreign_national_document.", "client_document"],
  ["foreign_national.", "client"],
  ["outgoing_intake.", "intake"],
  ["case_file.", "case_file"],
  ["case.", "case"],
  ["document.", "document"],
  ["questionnaire.", "questionnaire"],
  ["form.", "form"],
  ["draft.", "draft"],
  ["packet.", "packet"],
  ["filing_materials.", "packet"],
  ["artifact.", "artifact"],
  ["table.", "artifact"],
  ["recruitment.", "artifact"],
  ["research.", "research"],
  ["rfe_extraction.", "research"],
  ["agent.", "agent"]
];

const CREATED_VERBS = new Set([
  "created",
  "uploaded",
  "bundled_uploaded",
  "added",
  "copied",
  "copied_to_case",
  "generation_enqueued",
  "generation_completed",
  "imported",
  "folder_created",
  "started",
  "filled",
  "generated",
  "restored",
  "version_restored",
  "thread_created",
  "plan_created",
  "handoff",
  "answers_applied",
  "autocategorize_applied",
  "source_added",
  "rewrite_proposed",
  "comment_added",
  "file_uploaded",
  "table_created"
]);
const DELETED_VERBS = new Set([
  "deleted",
  "removed",
  "file_deleted",
  "issue_deleted",
  "source_removed",
  "thread_deleted"
]);
const SENT_VERBS = new Set([
  "sent",
  "reminder_sent",
  "intake_sent",
  "submitted",
  "opened"
]);
const EXPORTED_VERBS = new Set(["exported", "downloaded", "export_requested"]);

const actionSuffix = (action: string): string => {
  const a = str(action);
  const i = a.lastIndexOf(".");
  return i >= 0 ? a.slice(i + 1) : a;
};

export const entityKindFor = (action: string): ActivityEntityKind => {
  const a = str(action);
  for (const [prefix, kind] of ENTITY_PREFIXES) {
    if (a.startsWith(prefix)) return kind;
  }
  return "client";
};

export const actionKindFor = (action: string): ActivityActionKind => {
  const suffix = actionSuffix(action);
  if (CREATED_VERBS.has(suffix)) return "created";
  if (DELETED_VERBS.has(suffix)) return "deleted";
  if (SENT_VERBS.has(suffix)) return "sent";
  if (EXPORTED_VERBS.has(suffix)) return "exported";
  return "updated";
};

// Filtering -----------------------------------------------------------------

/** Feed "Type" pills; each maps onto one or more server `entity_kind`s. */
export type EntityTypeKey =
  | "personal_information"
  | "documents"
  | "case_files"
  | "cases"
  | "drafts"
  | "forms"
  | "questionnaire"
  | "packets"
  | "artifacts"
  | "intakes"
  | "agents"
  | "dependents"
  | "immigration_history"
  | "employment_history"
  | "address_history"
  | "reminders";

export type ActionKindKey = ActivityActionKind;
export type ActorKindKey = ActivityActorKind;

export type ActivityDateRange = "ALL" | "TODAY" | "THIS_WEEK" | "THIS_MONTH";

export const ENTITY_TYPE_OPTIONS: Array<{
  key: EntityTypeKey;
  label: string;
  kinds: ActivityEntityKind[];
}> = [
  { key: "personal_information", label: "Client Profile", kinds: ["client"] },
  { key: "cases", label: "Cases", kinds: ["case"] },
  {
    key: "documents",
    label: "Documents",
    kinds: ["document", "client_document"]
  },
  { key: "case_files", label: "Files", kinds: ["case_file"] },
  { key: "drafts", label: "Drafts", kinds: ["draft"] },
  { key: "forms", label: "Forms", kinds: ["form"] },
  { key: "questionnaire", label: "Questionnaire", kinds: ["questionnaire"] },
  { key: "packets", label: "Packets & Exports", kinds: ["packet"] },
  {
    key: "artifacts",
    label: "Artifacts & Research",
    kinds: ["artifact", "research"]
  },
  { key: "intakes", label: "Intakes", kinds: ["intake"] },
  { key: "agents", label: "Agents", kinds: ["agent"] },
  { key: "dependents", label: "Dependents", kinds: ["dependent"] },
  {
    key: "immigration_history",
    label: "Immigration History",
    kinds: ["immigration_history"]
  },
  {
    key: "employment_history",
    label: "Employment History",
    kinds: ["employment_history"]
  },
  {
    key: "address_history",
    label: "Address History",
    kinds: ["address_history"]
  },
  { key: "reminders", label: "Reminders", kinds: ["reminder"] }
];

const ENTITY_KIND_TO_TYPE: Record<ActivityEntityKind, EntityTypeKey> =
  ENTITY_TYPE_OPTIONS.reduce((acc, o) => {
    o.kinds.forEach((k) => {
      acc[k] = o.key;
    });
    return acc;
  }, {} as Record<ActivityEntityKind, EntityTypeKey>);

export const ACTION_KIND_OPTIONS: Array<{ key: ActionKindKey; label: string }> =
  [
    { key: "created", label: "Created" },
    { key: "updated", label: "Updated" },
    { key: "deleted", label: "Deleted" },
    { key: "sent", label: "Sent" },
    { key: "exported", label: "Exported" }
  ];

export const ACTOR_KIND_OPTIONS: Array<{ key: ActorKindKey; label: string }> = [
  { key: "user", label: "Team" },
  { key: "agent", label: "Agents" },
  { key: "client", label: "Client" },
  { key: "system", label: "System" }
];

export const DATE_RANGE_OPTIONS: Array<{
  key: ActivityDateRange;
  label: string;
}> = [
  { key: "ALL", label: "All Time" },
  { key: "TODAY", label: "Today" },
  { key: "THIS_WEEK", label: "This Week" },
  { key: "THIS_MONTH", label: "This Month" }
];

export type ActivityFilters = {
  query: string;
  dateRange: ActivityDateRange;
  entities: Record<EntityTypeKey, boolean>;
  actions: Record<ActionKindKey, boolean>;
  actors: Record<ActorKindKey, boolean>;
  /** A case (`individual_id`) to restrict to; null for the whole client. */
  caseId: string | null;
  /** Show bookkeeping events (see `isRoutineActivity`). Off by default. */
  routine: boolean;
};

const allOn = <K extends string>(keys: K[]): Record<K, boolean> =>
  keys.reduce((acc, k) => {
    acc[k] = true;
    return acc;
  }, {} as Record<K, boolean>);

/** Everything on: deleted events are visible by default. */
export const DEFAULT_ACTIVITY_FILTERS: ActivityFilters = {
  query: "",
  dateRange: "ALL",
  entities: allOn(ENTITY_TYPE_OPTIONS.map((o) => o.key)),
  actions: allOn(ACTION_KIND_OPTIONS.map((o) => o.key)),
  actors: allOn(ACTOR_KIND_OPTIONS.map((o) => o.key)),
  caseId: null,
  routine: false
};

export const getActionKind = (action: string): ActionKindKey =>
  actionKindFor(action);

export const getEntityType = (action: string): EntityTypeKey =>
  ENTITY_KIND_TO_TYPE[entityKindFor(action)] ?? "personal_information";

/** Server-side window for a date preset (client-side filtering does the rest). */
export const dateRangeWindow = (
  range: ActivityDateRange,
  now: dayjs.Dayjs = dayjs()
): { from?: string; to?: string } => {
  if (range === "ALL") return {};
  const unit =
    range === "TODAY" ? "day" : range === "THIS_WEEK" ? "week" : "month";
  return {
    from: now.startOf(unit).toDate().toISOString(),
    to: now.endOf(unit).toDate().toISOString()
  };
};

const selectedKeys = <K extends string>(
  record: Record<K, boolean>,
  all: K[]
): K[] | null => {
  const on = all.filter((k) => record[k]);
  return on.length === all.length ? null : on;
};

/**
 * The query params for a filter set. Only pills that exclude something are
 * sent; the text search stays client-side.
 */
export const filtersToQuery = (
  f: ActivityFilters,
  now: dayjs.Dayjs = dayjs()
): ListForeignNationalActivityLogParams => {
  const out: ListForeignNationalActivityLogParams = {
    ...dateRangeWindow(f.dateRange, now)
  };
  if (f.caseId) out.individual_id = f.caseId;
  const entities = selectedKeys(
    f.entities,
    ENTITY_TYPE_OPTIONS.map((o) => o.key)
  );
  if (entities) {
    const kinds = new Set<ActivityEntityKind>();
    ENTITY_TYPE_OPTIONS.filter((o) => entities.includes(o.key)).forEach((o) =>
      o.kinds.forEach((k) => kinds.add(k))
    );
    out.entity_kind = Array.from(kinds).join(",");
  }
  const actions = selectedKeys(
    f.actions,
    ACTION_KIND_OPTIONS.map((o) => o.key)
  );
  if (actions) out.action_kind = actions.join(",");
  const actors = selectedKeys(
    f.actors,
    ACTOR_KIND_OPTIONS.map((o) => o.key)
  );
  if (actors) out.actor_kind = actors.join(",");
  return out;
};

export const activeActivityFilterCount = (f: ActivityFilters): number =>
  [
    f.query.trim().length > 0,
    f.dateRange !== "ALL",
    Boolean(f.caseId),
    ENTITY_TYPE_OPTIONS.some((o) => !f.entities[o.key]),
    ACTION_KIND_OPTIONS.some((o) => !f.actions[o.key]),
    ACTOR_KIND_OPTIONS.some((o) => !f.actors[o.key])
  ].filter(Boolean).length;

// Formatting helpers ----------------------------------------------------------

const normalizeFieldKey = (value: string) =>
  (value ?? "").toString().trim().toLowerCase().replace(/\s+/g, "_");

export const findNewValue = (
  changes: Change[] | undefined,
  fieldKeys: string[]
): unknown => {
  if (!Array.isArray(changes) || changes.length === 0) return undefined;
  const wanted = fieldKeys.map(normalizeFieldKey);
  for (const c of changes) {
    if (wanted.includes(normalizeFieldKey(c.field))) return c.new ?? undefined;
  }
  return undefined;
};

const newString = (changes: Change[] | undefined, keys: string[]): string => {
  const v = findNewValue(changes, keys);
  return typeof v === "string" ? v.trim() : "";
};

export const coerceStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === "string" ? v.trim() : ""))
      .filter(Boolean);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    return trimmed.includes(",")
      ? trimmed
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [trimmed];
  }
  return [];
};

export const toTitleCase = (value: string): string =>
  (value ?? "")
    .toString()
    .trim()
    .replace(/[_-]+/g, " ")
    .split(/\s+/g)
    .filter(Boolean)
    .map((w) => `${w[0]?.toUpperCase() ?? ""}${w.slice(1).toLowerCase()}`)
    .join(" ");

export const formatOccurredAtLabel = (
  occurredAt?: string | null,
  now: dayjs.Dayjs = dayjs()
): string => {
  const d = dayjs(occurredAt ?? "");
  if (!d.isValid()) return "—";
  if (d.isSame(now, "day")) return `Today, ${d.format("h:mm A")}`;
  if (d.isSame(now.subtract(1, "day"), "day"))
    return `Yesterday, ${d.format("h:mm A")}`;
  return d.format("MMM D, YYYY, h:mm A");
};

/** "just now", "5m ago", "3h ago", "12d ago", "3mo ago", then a date. */
export const relativeTimeAgo = (
  occurredAt?: string | null,
  now: dayjs.Dayjs = dayjs()
): string => {
  const d = dayjs(occurredAt ?? "");
  if (!d.isValid()) return "—";
  const seconds = Math.max(0, now.diff(d, "second"));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = now.diff(d, "month");
  if (months < 12) return `${Math.max(1, months)}mo ago`;
  return d.format(d.year() === now.year() ? "MMM D" : "MMM D, YYYY");
};

/** The full timestamp shown in the time tooltip. */
export const absoluteTimeLabel = (occurredAt?: string | null): string => {
  const d = dayjs(occurredAt ?? "");
  return d.isValid() ? d.format("dddd, MMMM D, YYYY [at] h:mm A") : "—";
};

export const initialsFromLabel = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/[\s@._-]+/).filter(Boolean);
  return (
    `${parts[0]?.[0] ?? ""}${
      parts.length > 1 ? parts[1]?.[0] ?? "" : ""
    }`.toUpperCase() || "?"
  );
};

// Actor -----------------------------------------------------------------------

export type ActorInfo = {
  kind: ActorKindKey;
  /** @deprecated use `kind !== "user"`; kept for older callers. */
  isSystem: boolean;
  label: string;
  avatarUrl?: string;
  /** "Casey", "Word add-in" ... when an agent acted. */
  agentLabel?: string;
  /** The user an agent acted on behalf of, when known. */
  onBehalfOf?: string;
};

const AGENT_LABELS: Record<string, string> = {
  assistant: ASSISTANT_PERSONA,
  // Activity rows written before the merge.
  client_manager: ASSISTANT_PERSONA,
  case_assistant: ASSISTANT_PERSONA,
  practice_manager: ASSISTANT_PERSONA,
  word_addin: "Word add-in",
  drafting: "Drafting agent",
  research: "Research agent"
};

export const agentLabel = (agentKind?: string | null): string => {
  const k = str(agentKind);
  if (!k) return ASSISTANT_PERSONA;
  return AGENT_LABELS[k] ?? toTitleCase(k);
};

export const actorKindFor = (
  ev: Pick<
    ForeignNationalActivityLogEvent,
    "actor_kind" | "actor_user_id" | "actor_user_email"
  >
): ActorKindKey => {
  const k = str(ev.actor_kind);
  if (k === "user" || k === "agent" || k === "client" || k === "system")
    return k;
  return str(ev.actor_user_id) || str(ev.actor_user_email) ? "user" : "system";
};

const profileName = (
  uid: string,
  getUserProfile: UserProfileGetter
): { name: string; avatarUrl?: string } => {
  const profile = uid ? getUserProfile(uid) : undefined;
  const full = [profile?.first_name, profile?.last_name]
    .map((p) => (p ?? "").toString().trim())
    .filter(Boolean)
    .join(" ");
  return {
    name: full || (profile?.email ?? "").trim(),
    avatarUrl: profile?.avatar_data_url ?? undefined
  };
};

export const actorForEvent = (
  ev: Pick<
    ForeignNationalActivityLogEvent,
    | "actor_user_id"
    | "actor_user_email"
    | "actor_kind"
    | "actor_display_name"
    | "agent_kind"
  >,
  getUserProfile: UserProfileGetter
): ActorInfo => {
  const kind = actorKindFor(ev);
  const uid = str(ev.actor_user_id);
  const email = str(ev.actor_user_email);
  const display = str(ev.actor_display_name);
  const { name, avatarUrl } = profileName(uid, getUserProfile);

  if (kind === "agent") {
    const label = caseyName(display || agentLabel(ev.agent_kind));
    const who = name || email;
    return {
      kind,
      isSystem: true,
      label,
      agentLabel: label,
      onBehalfOf: who || undefined
    };
  }
  if (kind === "client")
    return { kind, isSystem: true, label: display || "Client" };
  if (kind === "system")
    return { kind, isSystem: true, label: display || "CaseBlink" };
  return {
    kind,
    isSystem: false,
    label: name || display || email || uid || "Team member",
    avatarUrl
  };
};

const actorKeyForEvent = (ev: ForeignNationalActivityLogEvent): string => {
  const uid = str(ev.actor_user_id);
  const kind = actorKindFor(ev);
  if (kind === "agent") return `agent:${str(ev.agent_kind)}:${uid}`;
  if (uid) return `uid:${uid}`;
  const email = str(ev.actor_user_email);
  if (email) return `email:${email.toLowerCase()}`;
  return kind;
};

// Legacy prose --------------------------------------------------------------------

const crudVerb = (a: string, created = "added") =>
  a.endsWith(".deleted")
    ? "deleted"
    : a.endsWith(".created")
    ? created
    : "updated";

/** Verb + generic subject for rows written before the server sent a `subject`. */
export const describeAction = (
  action: string
): { verb: string; subject: string } => {
  const a = (action ?? "").trim();
  if (a === "foreign_national.created")
    return { verb: "created", subject: "personal information" };
  if (a === "foreign_national.updated")
    return { verb: "updated", subject: "personal information" };
  if (a === "foreign_national.avatar_uploaded")
    return { verb: "uploaded", subject: "a profile photo" };
  if (a.startsWith("foreign_national.reminder."))
    return {
      verb: a.endsWith(".deleted")
        ? "deleted"
        : a.endsWith(".completed")
        ? "completed"
        : a.endsWith(".dismissed")
        ? "dismissed"
        : a.endsWith(".created")
        ? "added"
        : "updated",
      subject: "a reminder"
    };
  if (a.startsWith("foreign_national.dependent."))
    return { verb: crudVerb(a), subject: "a dependent" };
  if (a.startsWith("foreign_national.address_history."))
    return { verb: crudVerb(a), subject: "an address record" };
  if (a.startsWith("foreign_national.employment_history."))
    return { verb: crudVerb(a), subject: "an employment record" };
  if (a.startsWith("foreign_national.immigration_status_history."))
    return { verb: crudVerb(a), subject: "an immigration status record" };
  if (a.startsWith("foreign_national.immigration_entry_exit_history."))
    return { verb: crudVerb(a), subject: "an entry/exit record" };
  if (a.startsWith("document."))
    return {
      verb: a.endsWith(".deleted")
        ? "deleted"
        : a.endsWith(".updated")
        ? "updated"
        : a.endsWith(".downloaded")
        ? "downloaded"
        : "uploaded",
      subject: "a document"
    };
  if (a.startsWith("foreign_national_document.")) {
    if (a.endsWith(".copied_to_case"))
      return { verb: "copied", subject: "document(s) into a case" };
    if (a.endsWith(".version_restored"))
      return {
        verb: "restored an earlier version of",
        subject: "a client document"
      };
    return {
      verb: a.endsWith(".deleted")
        ? "deleted"
        : a.endsWith(".updated")
        ? "updated"
        : a.endsWith(".edited")
        ? "edited"
        : a.endsWith(".downloaded")
        ? "downloaded"
        : "uploaded",
      subject: "a client document"
    };
  }
  if (a.startsWith("outgoing_intake.")) {
    if (a.endsWith(".revision_requested"))
      return { verb: "requested revisions for", subject: "an intake" };
    const verb = a.endsWith(".sent")
      ? "sent"
      : a.endsWith(".opened")
      ? "opened"
      : a.endsWith(".file_uploaded")
      ? "uploaded"
      : a.endsWith(".submitted")
      ? "submitted"
      : a.endsWith(".accepted")
      ? "accepted"
      : a.endsWith(".deleted")
      ? "deleted"
      : a.endsWith(".updated")
      ? "updated"
      : "created";
    return {
      verb,
      subject: a.endsWith(".file_uploaded")
        ? "a file to an intake"
        : "an intake"
    };
  }
  if (a.startsWith("case."))
    return {
      verb: a.endsWith(".deleted")
        ? "deleted"
        : a.endsWith(".created")
        ? "created"
        : "updated",
      subject: "a case"
    };
  if (a.startsWith("draft.")) {
    const enqueued = a.endsWith(".generation_enqueued");
    if (a.endsWith(".generation_completed"))
      return { verb: "finished", subject: "a draft" };
    if (a.endsWith(".generation_failed"))
      return { verb: "could not finish", subject: "a draft" };
    return {
      verb: enqueued
        ? "requested"
        : a.endsWith(".deleted")
        ? "deleted"
        : a.endsWith(".updated")
        ? "updated"
        : "created",
      subject: enqueued ? "draft generation" : "a draft"
    };
  }
  if (a.startsWith("packet.")) {
    const enqueued = a.endsWith(".generation_enqueued");
    return {
      verb: enqueued ? "requested" : "updated",
      subject: enqueued ? "packet generation" : "a packet"
    };
  }
  return { verb: "updated", subject: a || "activity" };
};

// Deep links ------------------------------------------------------------------

export const eventCaseRef = (
  ev: ForeignNationalActivityLogEvent
): CaseRef | null => {
  const individualId = str(ev.individual_id);
  if (!individualId) return null;
  return {
    individualId,
    foreignNationalId: str(ev.foreign_national_id) || null
  };
};

/** `?tab=...&<focus>` relative to the current client profile. */
export const buildClientProfileHref = (
  tab: ClientTab,
  focus?: Record<string, string | undefined>,
  extras?: Record<string, string | undefined>
): string => {
  const params = new URLSearchParams();
  params.set("tab", tab);
  Object.entries({ ...(focus ?? {}), ...(extras ?? {}) }).forEach(([k, v]) => {
    const val = (v ?? "").toString().trim();
    if (k.trim() && val) params.set(k, val);
  });
  return `?${params.toString()}`;
};

export const isDocumentUploadAction = (action: unknown): boolean => {
  const a = typeof action === "string" ? action.trim() : "";
  return a === "document.uploaded" || a === "document.bundled_uploaded";
};

const draftTypeToLabel = (raw?: string | null): string => {
  const v = (raw ?? "").toString().trim();
  if (!v) return "";
  if (Object.prototype.hasOwnProperty.call(DRAFT_TYPE_LABELS, v))
    return DRAFT_TYPE_LABELS[v as DraftType];
  return toTitleCase(v);
};

/**
 * Where the subject of a legacy event links to (rows with a server `subject`
 * carry their own `path`): case > document > draft > packet > intake > client
 * sub-records. Null when there is nothing to open.
 */
export const eventSubjectHref = (
  ev: ForeignNationalActivityLogEvent
): string | null => {
  const serverPath = str(ev.subject?.path);
  if (serverPath) return serverPath;

  const action = str(ev.action);
  const changes = Array.isArray(ev.changes) ? ev.changes : [];
  const ref = eventCaseRef(ev);
  const documentId = str(ev.document_id);
  const isDocumentUpload = isDocumentUploadAction(action);
  const isDraftGeneration = action === "draft.generation_enqueued";

  if (ref && (Boolean(str(ev.case_id)) || action === "case.created"))
    return caseWorkspacePath(ref);
  if (!isDocumentUpload && documentId && ref) return caseWorkspacePath(ref);
  if (!isDraftGeneration && ev.draft_id && ref) return caseWorkspacePath(ref);
  if (ev.packet_id && ref) return caseWorkspacePath(ref);

  if (action.startsWith("outgoing_intake.")) {
    const oiId = newString(changes, [
      "outgoing_intake_id",
      "outgoingIntakeId",
      "oi_id",
      "oiId"
    ]);
    // Opens the intake in the Requests IDE; an intake awaiting review lands
    // on its latest submission there.
    return buildClientProfileHref(
      "outgoing_intakes",
      undefined,
      oiId ? { open: `intake:${oiId}` } : undefined
    );
  }

  if (str(ev.dependent_id))
    return buildClientProfileHref("dependents", {
      dependentId: str(ev.dependent_id)
    });
  if (str(ev.reminder_id))
    return buildClientProfileHref("reminders", {
      reminderId: str(ev.reminder_id)
    });
  if (str(ev.address_history_id))
    return buildClientProfileHref("address_history", {
      addressHistoryId: str(ev.address_history_id)
    });
  if (str(ev.employment_history_id))
    return buildClientProfileHref("employment", {
      employmentHistoryId: str(ev.employment_history_id)
    });
  if (str(ev.immigration_status_history_id))
    return buildClientProfileHref("immigration_overview", {
      immigrationStatusHistoryId: str(ev.immigration_status_history_id)
    });
  if (str(ev.immigration_entry_exit_history_id))
    return buildClientProfileHref("immigration_overview", {
      immigrationEntryExitHistoryId: str(ev.immigration_entry_exit_history_id)
    });
  if (ref && (isDocumentUpload || documentId)) return caseWorkspacePath(ref);
  return null;
};

// Normalization -------------------------------------------------------------------

export type NormalizedActivityEvent = ForeignNationalActivityLogEvent & {
  entity_kind: ActivityEntityKind;
  action_kind: ActivityActionKind;
  actor_kind: ActorKindKey;
  subject: ActivityEntityRef;
  target: ActivityEntityRef | null;
  case_label: string | null;
  count: number | null;
};

const legacySubjectLabel = (ev: ForeignNationalActivityLogEvent): string => {
  const action = str(ev.action);
  const changes = Array.isArray(ev.changes) ? ev.changes : [];
  if (action.startsWith("foreign_national.dependent.")) {
    const first = newString(changes, ["first_name", "firstname", "first"]);
    const last = newString(changes, ["last_name", "lastname", "last"]);
    const rel = toTitleCase(
      newString(changes, ["relationship", "relation"])
    ).toLowerCase();
    const who = [first, last].filter(Boolean).join(" ");
    if (who) return rel ? `${who} (${rel})` : who;
  }
  if (action.startsWith("foreign_national.reminder.")) {
    const title = newString(changes, ["title", "name", "message"]);
    if (title) return title;
  }
  if (action.startsWith("outgoing_intake.")) {
    const title = newString(changes, ["title"]);
    if (title) return title;
  }
  if (action === "draft.generation_enqueued") {
    const label = draftTypeToLabel(ev.draft_type ?? null);
    if (label) return `${label} draft`;
  }
  if (
    action.startsWith("document.") ||
    action.startsWith("foreign_national_document.")
  ) {
    const names = coerceStringArray(
      findNewValue(changes, [
        "docnames",
        "doc_names",
        "document_names",
        "document_name",
        "name",
        "title",
        "filename",
        "file_name"
      ])
    );
    if (names.length === 1) return names[0];
    if (names.length > 1)
      return `${names.slice(0, 2).join(", ")}${
        names.length > 2 ? ` +${names.length - 2} more` : ""
      }`;
  }
  if (action.startsWith("case.")) {
    const visa =
      str(ev.visa_type) ||
      newString(changes, ["visa", "visa_type", "visaType"]);
    if (visa) return `${visa} case`;
  }
  return "";
};

const legacySubjectKind = (ev: ForeignNationalActivityLogEvent): string => {
  const entity = entityKindFor(str(ev.action));
  if (entity === "client") return "client";
  return entity;
};

const legacySubjectId = (ev: ForeignNationalActivityLogEvent): string => {
  const candidates = [
    ev.document_id,
    ev.draft_id,
    ev.packet_id,
    ev.reminder_id,
    ev.dependent_id,
    ev.address_history_id,
    ev.employment_history_id,
    ev.immigration_status_history_id,
    ev.immigration_entry_exit_history_id
  ];
  for (const c of candidates) if (str(c)) return str(c);
  if (str(ev.action).startsWith("case.")) return str(ev.individual_id);
  return str(ev.foreign_national_id);
};

/**
 * Bring any row, including ones written before the server denormalized
 * `entity_kind`/`actor_kind`/`subject`, onto the current shape. Idempotent.
 */
export const normalizeEvent = (
  ev: ForeignNationalActivityLogEvent
): NormalizedActivityEvent => {
  const action = str(ev.action);
  const entity_kind =
    (str(ev.entity_kind) as ActivityEntityKind) || entityKindFor(action);
  const action_kind =
    (str(ev.action_kind) as ActivityActionKind) || actionKindFor(action);
  const actor_kind = actorKindFor(ev);
  const legacySubject = !ev.subject || !str(ev.subject.kind);
  const subject: ActivityEntityRef = legacySubject
    ? {
        kind: legacySubjectKind(ev),
        id: legacySubjectId(ev) || null,
        label: legacySubjectLabel(ev) || null,
        path: eventSubjectHref(ev)
      }
    : {
        kind: str(ev.subject?.kind),
        id: str(ev.subject?.id) || null,
        label: str(ev.subject?.label) || null,
        path: str(ev.subject?.path) || eventSubjectHref(ev)
      };
  const case_label = str(ev.case_label) || str(ev.visa_type) || null;
  const ref = eventCaseRef(ev);
  const target: ActivityEntityRef | null =
    ev.target && str(ev.target.kind)
      ? {
          kind: str(ev.target.kind),
          id: str(ev.target.id) || null,
          label: str(ev.target.label) || null,
          path: str(ev.target.path) || null
        }
      : ref && subject.kind !== "case"
      ? {
          kind: "case",
          id: ref.individualId,
          label: case_label,
          path: caseWorkspacePath(ref)
        }
      : null;
  const count =
    typeof ev.count === "number" && Number.isFinite(ev.count) && ev.count > 1
      ? Math.floor(ev.count)
      : null;
  return {
    ...ev,
    action,
    entity_kind,
    action_kind,
    actor_kind,
    subject,
    target,
    case_label,
    count
  };
};

// Prose -----------------------------------------------------------------------

/** Verb phrases keyed by the action suffix (`uploaded` in `document.uploaded`). */
const VERBS: Record<string, string> = {
  created: "created",
  updated: "updated",
  deleted: "deleted",
  uploaded: "uploaded",
  bundled_uploaded: "uploaded a bundle of",
  downloaded: "downloaded",
  exported: "exported",
  sent: "sent",
  reminder_sent: "sent a reminder for",
  opened: "opened",
  submitted: "submitted",
  accepted: "accepted",
  revision_requested: "requested changes to",
  answers_applied: "applied answers from",
  file_uploaded: "uploaded a file to",
  file_deleted: "removed a file from",
  completed: "completed",
  dismissed: "dismissed",
  avatar_uploaded: "changed the photo on",
  portal_access_updated: "changed portal access for",
  portal_invite_sent: "sent a portal invite to",
  portal_invite_accepted: "first signed in to",
  client_view_updated: "changed what the client sees on",
  correction_proposed: "suggested a correction to",
  correction_withdrawn: "withdrew a correction to",
  correction_accepted: "accepted a correction to",
  correction_declined: "declined a correction to",
  signature_requested: "asked for a signature on",
  signature_request_opened: "sent for signature",
  signature_request_cancelled: "cancelled the signature request for",
  document_signed: "signed",
  ink_signature_submitted: "sent the ink-signed page of",
  ink_signature_reviewed: "reviewed the ink signature on",
  duplicate_merged: "merged a duplicate record into",
  archived: "archived",
  unarchived: "restored",
  warning_dismissed: "dismissed",
  warning_restored: "restored",
  copied_to_case: "sent to a case",
  status_updated: "changed the status of",
  added: "added",
  removed: "removed",
  folder_created: "created a folder in",
  renamed: "renamed",
  moved: "moved",
  split: "split",
  merged: "merged",
  pdf_edited: "edited the pages of",
  exhibit_scheme_updated: "changed the exhibit scheme of",
  version_restored: "restored a version of",
  imported: "imported files into",
  autocategorize_applied: "auto-organized",
  source_added: "added a source to",
  source_removed: "removed a source from",
  filled: "filled",
  generated: "generated",
  restored: "restored",
  intake_sent: "sent an intake for",
  generation_enqueued: "generated",
  generation_completed: "finished",
  generation_failed: "could not finish",
  docx_updated: "edited",
  changes_resolved: "resolved tracked changes in",
  rewrite_proposed: "proposed a rewrite of",
  comment_added: "commented on",
  word_addin_edited: "edited in Word",
  settings_updated: "changed settings on",
  export_requested: "requested an export of",
  copied: "copied",
  table_created: "created a table for",
  started: "started",
  issue_deleted: "removed an issue from",
  thread_created: "started a conversation about",
  thread_deleted: "deleted a conversation about",
  plan_created: "wrote a plan for",
  handoff: "handed off work on",
  link_requested: "asked for a new link to",
  edited: "edited",
  uscis_receipt_added: "added a USCIS receipt to",
  uscis_receipt_removed: "removed a USCIS receipt from",
  company_linked: "linked a company to",
  company_unlinked: "unlinked the company from",
  company_updated: "updated the petitioner on",
  playbook_updated: "changed the company playbook on",
  billing_terms_updated: "changed the company fee terms on",
  company_request_accepted: "opened from a company request",
  company_approval_sent: "asked the company to approve something on",
  company_approval_answered: "answered the company approval on",
  company_message_received: "received a company message on",
  company_message_sent: "replied to the company on",
  delegated_work: `had ${ASSISTANT_PERSONA} work on`,
  change_undone: `undid ${ASSISTANT_PERSONA}'s change to`,
  offer_made: "made an offer about",
  offer_decided: `answered ${ASSISTANT_PERSONA}'s offer about`,
  title_corrected: `corrected ${ASSISTANT_PERSONA}'s title for`
};

const ENTITY_NOUNS: Record<ActivityEntityKind, string> = {
  client: "the client profile",
  reminder: "a reminder",
  dependent: "a dependent",
  address_history: "an address",
  employment_history: "an employment record",
  immigration_history: "an immigration record",
  client_document: "a client document",
  intake: "an intake",
  case: "a case",
  document: "a document",
  case_file: "a file",
  questionnaire: "the questionnaire",
  form: "a form",
  draft: "a draft",
  packet: "the packet",
  artifact: "an artifact",
  research: "research",
  agent: "an agent thread"
};

const PLURAL_NOUNS: Partial<Record<ActivityEntityKind, string>> = {
  document: "documents",
  client_document: "documents",
  case_file: "files",
  form: "forms",
  draft: "drafts",
  reminder: "reminders",
  dependent: "dependents",
  artifact: "artifacts",
  intake: "intakes",
  research: "research items"
};

export const verbFor = (action: string): string => {
  const suffix = actionSuffix(action);
  return VERBS[suffix] ?? (suffix.replace(/_/g, " ") || "changed");
};

export type ChangeLineKind = "added" | "updated" | "removed";

/** One Clarify-style sub-line: `Field -> Added|Updated|Removed value`. */
export type ChangeLine = {
  field: string;
  kind: ChangeLineKind;
  value: string;
  oldValue?: string;
};

const isEmptyValue = (v: unknown): boolean =>
  v == null ||
  (typeof v === "string" && v.trim() === "") ||
  (Array.isArray(v) && v.length === 0);

/** Machine references (`id`, `*_id`, `*_ids`) say nothing to a reader; hide them. */
const isMachineField = (key: string): boolean =>
  key === "id" || key.endsWith("_id") || key.endsWith("_ids");

// `2026-09-22T13:23:32.799757+00:00` (the server also writes a space for the
// `T`) or a bare `2026-09-22`, and nothing else.
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A change value as a person would read it: Yes/No, friendly dates, else the raw label. */
export const changeValueLabel = (value: unknown): string => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") {
    const s = value.trim();
    if (ISO_TIMESTAMP.test(s)) {
      // Normalise to strict ISO: `T` separator, at most millisecond precision.
      const d = dayjs(s.replace(" ", "T").replace(/(\.\d{3})\d+/, "$1"));
      if (d.isValid()) return d.format("MMM D, YYYY, h:mm A");
    } else if (ISO_DATE.test(s)) {
      const d = dayjs(s);
      if (d.isValid()) return d.format("MMM D, YYYY");
    }
  }
  return safeValueLabel(value);
};

export const changeLinesFor = (
  changes: Change[] | undefined,
  changedFields?: string[]
): ChangeLine[] => {
  const out: ChangeLine[] = [];
  const seen = new Set<string>();
  (Array.isArray(changes) ? changes : []).forEach((c) => {
    const field = str(c.field);
    if (!field) return;
    const key = normalizeFieldKey(field);
    seen.add(key);
    if (isMachineField(key)) return;
    const oldEmpty = isEmptyValue(c.old);
    const newEmpty = isEmptyValue(c.new);
    if (oldEmpty && newEmpty) return;
    if (oldEmpty)
      out.push({
        field: toTitleCase(field),
        kind: "added",
        value: changeValueLabel(c.new)
      });
    else if (newEmpty)
      out.push({
        field: toTitleCase(field),
        kind: "removed",
        value: changeValueLabel(c.old)
      });
    else
      out.push({
        field: toTitleCase(field),
        kind: "updated",
        value: changeValueLabel(c.new),
        oldValue: changeValueLabel(c.old)
      });
  });
  (changedFields ?? []).forEach((f) => {
    const field = str(f);
    if (!field) return;
    const key = normalizeFieldKey(field);
    if (seen.has(key) || isMachineField(key)) return;
    seen.add(key);
    out.push({ field: toTitleCase(field), kind: "updated", value: "" });
  });
  return out;
};

export type BatchItem = { id: string; label: string; href: string | null };

/** The items behind a `count > 1` row (`metadata.documents`, `.nodes`, ...). */
export const batchItemsFor = (
  ev: ForeignNationalActivityLogEvent
): BatchItem[] => {
  const meta = (ev.metadata ?? {}) as Record<string, unknown>;
  const ref = eventCaseRef(ev);
  const lists = ["documents", "nodes", "files", "items", "forms", "drafts"];
  const key = lists.find(
    (k) => Array.isArray(meta[k]) && (meta[k] as unknown[]).length > 0
  );
  if (key) {
    const raw = meta[key] as unknown[];
    return raw
      .map((item, i) => {
        if (typeof item === "string")
          return { id: `${key}-${i}`, label: item.trim(), href: null };
        if (item && typeof item === "object") {
          const rec = item as Record<string, unknown>;
          const id = str(rec.id) || `${key}-${i}`;
          const label =
            str(rec.title) || str(rec.name) || str(rec.label) || "Untitled";
          return {
            id,
            label,
            href: key === "documents" && ref ? caseWorkspacePath(ref) : null
          };
        }
        return null;
      })
      .filter((x): x is BatchItem => Boolean(x && x.label));
  }
  const legacyNames = coerceStringArray(
    findNewValue(Array.isArray(ev.changes) ? ev.changes : [], [
      "docnames",
      "doc_names",
      "document_names"
    ])
  );
  return legacyNames.map((name, i) => ({
    id: `legacy-${i}`,
    label: name,
    href: ref ? caseWorkspacePath(ref) : null
  }));
};

export type DescribedEvent = {
  actor: ActorInfo;
  verb: string;
  subject: { label: string; href: string | null; kind: string };
  /** "in the H-1B case" — omitted when the subject is the case itself. */
  target: { label: string; href: string | null } | null;
  /** `count` for batches; null otherwise. */
  count: number | null;
  changeLines: ChangeLine[];
  batchItems: BatchItem[];
  /** Case-sensitive words the text search matches against. */
  searchText: string;
};

/** `3 documents`, `4 files`: the subject label for a batch or a group. */
export const countLabel = (
  count: number,
  entity: ActivityEntityKind
): string => {
  const noun =
    PLURAL_NOUNS[entity] ??
    `${ENTITY_NOUNS[entity].replace(/^(a|an|the)\s+/, "")}s`;
  return `${count} ${noun}`;
};

/**
 * One row's prose: `Actor verb Subject [in the H-1B case]` plus the change
 * lines and batch children underneath.
 */
export const describeEvent = (
  raw: ForeignNationalActivityLogEvent,
  getUserProfile: UserProfileGetter
): DescribedEvent => {
  const ev = normalizeEvent(raw);
  const actor = actorForEvent(ev, getUserProfile);
  const legacy = !raw.subject || !str(raw.subject.kind);
  const changes = Array.isArray(ev.changes) ? ev.changes : [];

  let verb = verbFor(ev.action);
  let subjectLabel = str(ev.subject.label);
  if (legacy && !subjectLabel) {
    const d = describeAction(ev.action);
    verb = d.verb;
    subjectLabel = d.subject;
  }
  if (ev.count && ev.count > 1) {
    subjectLabel = countLabel(ev.count, ev.entity_kind);
  } else if (!subjectLabel) {
    subjectLabel = ENTITY_NOUNS[ev.entity_kind] ?? "an item";
  }

  const batchItems = ev.count && ev.count > 1 ? batchItemsFor(ev) : [];
  const changeLines =
    ev.action_kind === "deleted"
      ? []
      : changeLinesFor(changes, ev.changed_fields);

  const target =
    ev.target && ev.subject.kind !== "case"
      ? {
          label: str(ev.target.label) || "case",
          href: str(ev.target.path) || null
        }
      : null;

  const searchText = [
    actor.label,
    actor.onBehalfOf ?? "",
    verb,
    subjectLabel,
    target?.label ?? "",
    ev.action,
    ev.actor_user_email ?? "",
    ...changeLines.map((l) => `${l.field} ${l.value} ${l.oldValue ?? ""}`),
    ...batchItems.map((b) => b.label)
  ]
    .join(" ")
    .toLowerCase();

  return {
    actor,
    verb,
    subject: {
      label: subjectLabel,
      href: str(ev.subject.path) || null,
      kind: ev.subject.kind
    },
    target,
    count: ev.count,
    changeLines,
    batchItems,
    searchText
  };
};

/** `Ada uploaded 3 documents in the H-1B case` — for aria labels and tests. */
export const summarizeEvent = (
  ev: ForeignNationalActivityLogEvent,
  getUserProfile: UserProfileGetter = () => undefined
): string => {
  const d = describeEvent(ev, getUserProfile);
  return [
    d.actor.label,
    d.verb,
    d.subject.label,
    d.target
      ? `in the ${d.target.label}${
          d.target.label.toLowerCase().endsWith("case") ? "" : " case"
        }`
      : ""
  ]
    .filter(Boolean)
    .join(" ");
};

// Client-side filtering -----------------------------------------------------------

const NO_PROFILES: UserProfileGetter = () => undefined;

/** Bookkeeping that rarely matters when reading a client's history. */
const ROUTINE_ACTIONS = new Set([
  "agent.thread_created",
  "agent.thread_deleted",
  "agent.plan_created",
  "agent.offer_made",
  "agent.handoff",
  "outgoing_intake.opened",
  "outgoing_intake.link_requested",
  "outgoing_intake.reminder_sent",
  "key_fact.updated",
  "case_file.autocategorize_applied",
  "case_file.title_corrected",
  "document.downloaded",
  "foreign_national_document.downloaded",
  "form.downloaded"
]);

/** Conversations started, links opened, downloads and automatic upkeep. */
export const isRoutineActivity = (
  raw: ForeignNationalActivityLogEvent
): boolean => {
  const ev = normalizeEvent(raw);
  return ROUTINE_ACTIONS.has(str(ev.action)) || ev.actor_kind === "system";
};

export const filterActivity = (
  events: ForeignNationalActivityLogEvent[],
  f: ActivityFilters,
  getUserProfile: UserProfileGetter = NO_PROFILES
): ForeignNationalActivityLogEvent[] => {
  const q = f.query.trim().toLowerCase();
  return events.filter((raw) => {
    const ev = normalizeEvent(raw);
    const type = ENTITY_KIND_TO_TYPE[ev.entity_kind] ?? "personal_information";
    if (!f.entities[type]) return false;
    if (!f.actions[ev.action_kind]) return false;
    if (!f.actors[ev.actor_kind]) return false;
    if (f.caseId && str(ev.individual_id) !== f.caseId) return false;
    if (!f.routine && isRoutineActivity(ev)) return false;
    if (!q) return true;
    return describeEvent(raw, getUserProfile).searchText.includes(q);
  });
};

// Icons -----------------------------------------------------------------------------

const ENTITY_ICONS: Record<ActivityEntityKind, LucideIcon> = {
  client: User,
  reminder: CalendarClock,
  dependent: Users,
  address_history: Home,
  employment_history: Briefcase,
  immigration_history: Plane,
  client_document: FileText,
  intake: ClipboardList,
  case: Globe,
  document: FileText,
  case_file: FolderTree,
  questionnaire: ListChecks,
  form: FileSpreadsheet,
  draft: PenLine,
  packet: Package,
  artifact: FileSpreadsheet,
  research: Search,
  agent: MessageSquare
};

/** The rail icon for a row: the entity, with a few action overrides. */
export const iconForEvent = (
  ev: ForeignNationalActivityLogEvent
): LucideIcon => {
  const n = normalizeEvent(ev);
  const suffix = actionSuffix(n.action);
  if (n.entity_kind === "agent") return Bot;
  if (
    suffix === "sent" ||
    suffix === "reminder_sent" ||
    suffix === "intake_sent" ||
    suffix === "portal_invite_sent"
  )
    return Mail;
  if (
    suffix === "settings_updated" ||
    suffix === "portal_access_updated" ||
    suffix === "client_view_updated"
  )
    return Settings2;
  if (suffix.startsWith("correction_")) return PenLine;
  if (suffix.startsWith("warning_")) return AlertTriangle;
  if (
    suffix.startsWith("signature_") ||
    suffix === "document_signed" ||
    suffix.startsWith("ink_signature_")
  )
    return FileSignature;
  return ENTITY_ICONS[n.entity_kind] ?? FileText;
};

export const iconForActorKind = (kind: ActorKindKey): LucideIcon =>
  kind === "agent" ? Bot : kind === "client" ? User : Settings2;

// Grouping ----------------------------------------------------------------------------

export type ActivityRenderItem =
  | { type: "event"; ev: ForeignNationalActivityLogEvent }
  | {
      type: "group";
      key: string;
      action: string;
      /** Items touched across the run (each event's `count`, or 1). */
      count: number;
      events: ForeignNationalActivityLogEvent[];
    };

export const GROUP_MIN_RUN = 3;
export const GROUP_WINDOW_MINUTES = 10;

const groupKeyFor = (ev: ForeignNationalActivityLogEvent): string =>
  [actorKeyForEvent(ev), str(ev.action), str(ev.individual_id)].join("|");

const withinWindow = (
  a: ForeignNationalActivityLogEvent,
  b: ForeignNationalActivityLogEvent
): boolean => {
  const prev = dayjs(a.occurred_at ?? "");
  const next = dayjs(b.occurred_at ?? "");
  if (!prev.isValid() || !next.isValid()) return false;
  return Math.abs(prev.diff(next, "minute", true)) <= GROUP_WINDOW_MINUTES;
};

/**
 * Consecutive events by the same actor, with the same action, in the same
 * case, each within ten minutes of the previous one, collapse into one group
 * once the run reaches three. Rows the server already batched (`count > 1`)
 * are kept as they are unless they too repeat.
 */
export const groupActivity = (
  events: ForeignNationalActivityLogEvent[]
): ActivityRenderItem[] => {
  const out: ActivityRenderItem[] = [];
  let run: ForeignNationalActivityLogEvent[] = [];
  let runKey = "";

  const flush = () => {
    if (run.length === 0) return;
    if (run.length >= GROUP_MIN_RUN) {
      out.push({
        type: "group",
        key: `group:${run[0].activity_id}`,
        action: str(run[0].action),
        count: run.reduce(
          (sum, ev) => sum + (normalizeEvent(ev).count ?? 1),
          0
        ),
        events: run
      });
    } else {
      run.forEach((ev) => out.push({ type: "event", ev }));
    }
    run = [];
    runKey = "";
  };

  events.forEach((ev) => {
    const key = groupKeyFor(ev);
    const last = run[run.length - 1];
    if (run.length > 0 && (key !== runKey || !withinWindow(last, ev))) flush();
    if (run.length === 0) runKey = key;
    run.push(ev);
  });
  flush();
  return out;
};

/** @deprecated alias of `groupActivity`. */
export const buildActivityItems = groupActivity;

// Day sections ----------------------------------------------------------------------

export type ActivityDaySection = {
  key: string;
  label: string;
  items: ActivityRenderItem[];
};

const itemTime = (item: ActivityRenderItem): string | null | undefined =>
  item.type === "event" ? item.ev.occurred_at : item.events[0]?.occurred_at;

/** "Today", "Yesterday", then "Monday, September 7" style headers. */
export const dayLabel = (
  occurredAt?: string | null,
  now: dayjs.Dayjs = dayjs()
): string => {
  const d = dayjs(occurredAt ?? "");
  if (!d.isValid()) return "Earlier";
  if (d.isSame(now, "day")) return "Today";
  if (d.isSame(now.subtract(1, "day"), "day")) return "Yesterday";
  return d.format(d.isSame(now, "year") ? "dddd, MMMM D" : "MMMM D, YYYY");
};

export const sectionByDay = (
  items: ActivityRenderItem[],
  now: dayjs.Dayjs = dayjs()
): ActivityDaySection[] => {
  const out: ActivityDaySection[] = [];
  items.forEach((item) => {
    const t = dayjs(itemTime(item) ?? "");
    const key = t.isValid() ? t.format("YYYY-MM-DD") : "unknown";
    const last = out[out.length - 1];
    if (last && last.key === key) last.items.push(item);
    else out.push({ key, label: dayLabel(itemTime(item), now), items: [item] });
  });
  return out;
};

/** Case options for the Case pill, derived from what's in the feed plus the client's cases. */
export type ActivityCaseOption = { id: string; label: string };

export const caseOptionsFrom = (
  cases: Array<{
    id?: string | null;
    uid?: string | null;
    visa?: string | null;
    label?: string | null;
  }>,
  events: ForeignNationalActivityLogEvent[]
): ActivityCaseOption[] => {
  const byId = new Map<string, string>();
  cases.forEach((c) => {
    const id = str(c.id) || str(c.uid);
    if (!id) return;
    byId.set(id, str(c.label) || str(c.visa) || "Case");
  });
  events.forEach((ev) => {
    const id = str(ev.individual_id);
    if (!id || byId.has(id)) return;
    byId.set(id, str(ev.case_label) || str(ev.visa_type) || "Case");
  });
  return Array.from(byId.entries()).map(([id, label]) => ({ id, label }));
};
